/**
 * Phase 67 — Liste des devis traiteur événementiels.
 * Route : /devis
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useConfirm } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Plus, FileText, Search, Calendar, MapPin, Users, Loader2, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  BROUILLON: { label: "Brouillon", cls: "bg-[#F4F4F0] text-[#575A57] border-[#E5E5E0]" },
  ENVOYE: { label: "Envoyé", cls: "bg-[#EAF1F8] text-[#2E5C8A] border-[#C5D9E8]" },
  ACCEPTE: { label: "Accepté", cls: "bg-[#EAF4EE] text-[#1F4E3D] border-[#C5DBCF]" },
  REFUSE: { label: "Refusé", cls: "bg-[#FCEEEA] text-[#C05C47] border-[#E8C1B4]" },
  EXPIRE: { label: "Expiré", cls: "bg-[#F0F0EB] text-[#737373] border-[#E5E5E0]" },
  ANNULE: { label: "Annulé", cls: "bg-[#F0F0EB] text-[#737373] border-[#E5E5E0]" },
  CONVERTI: { label: "Facturé", cls: "bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D9B8]" },
};

export default function CateringQuotes() {
  const confirm = useConfirm();
  const [quotes, setQuotes] = useState(null);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [creating, setCreating] = useState(false);
  // Phase 96e — multi-select admin
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const toggleSelect = (id) => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const bulkForceDelete = async () => {
    if (selectedIds.size === 0) return;
    const ok = await confirm({
      title: `Supprimer ${selectedIds.size} devis sélectionné(s) ?`,
      description: "Action IRRÉVERSIBLE. Les devis CONVERTI (facturés) seront refusés — annulez d'abord la conversion depuis la fiche du devis.",
      confirmText: "Oui, supprimer",
      destructive: true,
    });
    if (!ok) return;
    setBulkDeleting(true);
    try {
      const r = await api.post("/admin/catering-quotes/bulk-force-delete", { ids: Array.from(selectedIds) });
      const parts = [`${r.data.deleted} supprimés`];
      if (r.data.refused_converti) parts.push(`${r.data.refused_converti} refusé(s) car facturé(s)`);
      toast.success(parts.join(" · "));
      setSelectedIds(new Set());
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur suppression en masse");
    } finally {
      setBulkDeleting(false);
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/catering-quotes" + (filterStatus ? `?status=${filterStatus}` : ""));
      setQuotes(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur chargement devis");
      setQuotes([]);
    }
  };

  useEffect(() => {
    load();
  }, [filterStatus]);

  const createNew = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/catering-quotes", {
        client_name: "Nouveau client",
        lines: [],
      });
      window.location.href = `/devis/${data.id}`;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur création");
      setCreating(false);
    }
  };

  const filtered = (quotes || []).filter((q) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (q.reference || "").toLowerCase().includes(s) ||
      (q.client_name || "").toLowerCase().includes(s) ||
      (q.client_company || "").toLowerCase().includes(s) ||
      (q.event_location || "").toLowerCase().includes(s)
    );
  });

  const fmtCHF = (n) => `${(Number(n) || 0).toFixed(2)} CHF`;
  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("fr-CH", { day: "2-digit", month: "short", year: "numeric" }); }
    catch { return iso; }
  };

  return (
    <div className="space-y-6" data-testid="catering-quotes-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-[#1A1C1A]">Devis traiteur</h1>
          <p className="text-sm text-[#575A57] mt-1">Offres événementielles avec conversion 1-clic en facture</p>
        </div>
        <Button onClick={createNew} disabled={creating} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="catering-quote-create">
          {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
          Nouveau devis
        </Button>
      </div>

      <Card className="border-[#E5E5E0]">
        <CardContent className="p-4 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A3A6A3]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Référence, client, lieu…"
              className="pl-9"
              data-testid="catering-quote-search"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="border border-[#E5E5E0] rounded px-3 py-2 text-sm bg-white"
            data-testid="catering-quote-filter-status"
          >
            <option value="">Tous statuts</option>
            {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </CardContent>
      </Card>

      {quotes === null && (
        <div className="flex items-center gap-2 text-sm text-[#575A57]">
          <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
        </div>
      )}

      {quotes && filtered.length === 0 && (
        <Card className="border-[#E5E5E0]">
          <CardContent className="p-8 text-center text-sm text-[#575A57]" data-testid="catering-quotes-empty">
            {search || filterStatus ? "Aucun devis trouvé avec ces filtres." : "Aucun devis pour le moment. Créez-en un avec « Nouveau devis »."}
          </CardContent>
        </Card>
      )}

      {/* Bandeau bulk actions (admin) */}
      {isAdmin && selectedIds.size > 0 && (
        <Card className="border-[#E8C1B4] bg-[#FCEEEA]" data-testid="quote-bulk-actions-bar">
          <CardContent className="py-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm text-[#8C3A2A]">
              <AlertTriangle className="w-4 h-4" />
              <span><strong>{selectedIds.size}</strong> devis sélectionné{selectedIds.size > 1 ? "s" : ""}</span>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setSelectedIds(new Set())} data-testid="quote-clear-selection">Désélectionner</Button>
              <Button size="sm" variant="destructive" onClick={bulkForceDelete} disabled={bulkDeleting} data-testid="quote-bulk-delete">
                <Trash2 className="w-4 h-4 mr-1" />
                {bulkDeleting ? "Suppression…" : `Supprimer ${selectedIds.size}`}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {filtered.length > 0 && (
        <div className="grid gap-3">
          {filtered.map((q) => {
            const meta = STATUS_META[q.status] || STATUS_META.BROUILLON;
            const totalTtc = q.totals?.total_ttc || 0;
            const isConverti = q.status === "CONVERTI" || q.invoice_id;
            const canSelect = isAdmin && !isConverti;
            return (
              <Card key={q.id} className="border-[#E5E5E0] hover:border-[#1F4E3D] transition-colors" data-testid={`catering-quote-row-${q.reference}`}>
                <CardContent className="p-4 flex items-center gap-3 flex-wrap">
                  {isAdmin && (
                    <input
                      type="checkbox"
                      checked={selectedIds.has(q.id)}
                      disabled={!canSelect}
                      onChange={(e) => { e.stopPropagation(); toggleSelect(q.id); }}
                      onClick={(e) => e.stopPropagation()}
                      className={`cursor-pointer accent-[#1F4E3D] ${!canSelect ? "opacity-30" : ""}`}
                      title={isConverti ? "Devis facturé — annulez d'abord la conversion" : "Sélectionner"}
                      data-testid={`quote-select-${q.id}`}
                    />
                  )}
                  <Link to={`/devis/${q.id}`} className="flex-1 flex items-center justify-between gap-4 min-w-0">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-sm text-[#1A1C1A] font-semibold">{q.reference}</span>
                        <Badge className={`${meta.cls} text-[10px] uppercase tracking-wider`}>{meta.label}</Badge>
                        {q.invoice_id && <Badge className="bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D9B8] text-[10px]"><FileText className="w-3 h-3 mr-1" />Facturé</Badge>}
                      </div>
                      <p className="text-sm text-[#1A1C1A] mt-1.5">
                        {q.client_company ? <><span className="font-medium">{q.client_company}</span> · </> : null}
                        {q.client_name}
                      </p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-[#575A57] flex-wrap">
                        {q.event_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{fmtDate(q.event_date)}</span>}
                        {q.event_location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{q.event_location}</span>}
                        {q.event_guests_count && <span className="flex items-center gap-1"><Users className="w-3 h-3" />{q.event_guests_count} pers.</span>}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-base font-semibold text-[#1F4E3D]">{fmtCHF(totalTtc)}</div>
                      <div className="text-[10px] text-[#A3A6A3]">{(q.lines || []).length} ligne(s)</div>
                    </div>
                  </Link>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
